/*
  A wrapper to history.state so multiple components/modules can use it without overwriting each others state data

  Note: if you are using historyState in a page, then you should always use it - specifically, if you use 
  historyState and then later call history.pushState instead of historyState.pushState then historyState may break

  usage:
    instead of;
      histor.replaceState('MY DATA', document.title, location.href)
    use;
      historyState.set('my key', 'MY DATA')
    
    instead of;
      histor.pushState('MY DATA', document.title, location.href)
    use;
      historyState.pushState(document.title, location.href)
      historyState.set('my key', 'MY DATA')

    fetch state with;
      const myState = historyState.get('my key')
*/
import debounce from 'debounce'

class HistoryState {

  constructor(){

    //safari throws security errors for push/replaceState calls more than 100 per 30secs 
    const stateChangeDebounceDelayMillisecs = 330;
    this.saveState = debounce(this.saveState.bind(this), stateChangeDebounceDelayMillisecs, true);
    this.state = {};
    
    //e.g. in unit tests
    if(typeof window !== 'undefined'){
      this.init();
    }
  }

  init(){
    this.loadState();
    window.addEventListener("beforeunload", this.onPageUnload.bind(this));
    window.addEventListener("popstate", this.loadState.bind(this));
  }

  onPageUnload(){
    this.saveState();
    this.saveState.flush();
  }

  loadState(){
    this.state = window.history.state || {};
    if(typeof this.state !== 'object'){
      console.warn('history.state was not an object. value: ', window.history.state, ' not expected. history.replaceState or history.pushState are likely being called directly instead of historyState versions');
      this.state = {};
    }
  }

  saveState(){
    window.history.replaceState(this.state, document.title, window.location.href);
  }

  get(key){
    return this.state[key];
  }

  set(key, value){
    this.state[key] = value;
    //when popstate events occure these is no 'before-pop' event for us to save current state - so we 
    //have to keep history.state up to date on every set
    this.saveState();
  }

  remove(key){
    delete this.state[key];
  }

  pushState(title, url){
    this.saveState();
    this.saveState.flush();    
    window.history.pushState(undefined, title, url);
    this.loadState();
  }

  replaceState(title, url){
    window.history.replaceState(undefined, title, url);
  }

  go(index){
    this.saveState();
    window.history.go(index);
    //load handled by popstate event
  }

  back(){
    this.saveState();
    window.history.back();
    //load handled by popstate event
  }

  forward(){
    this.saveState();
    window.history.forward();
    this.loadState();
  }
}


export default new HistoryState();